import React, { useCallback, useEffect } from 'react';
import { Typography, Stack } from '@worthy-npm/worthy-common-ui-components';
import { debounce } from 'lodash';
import { StepContainer, StepGridContainer, Step } from '../../../styles/common';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  updateSecondarySubset,
  updateStyle,
  selectStyle,
  selectItemType,
} from '../../../slices/submitSlice';
import { StepProps, StyleI } from '../common';
import { parametrize } from '../../../lib/commonUtils';
import StepTitle from '../../stepTitle';
import { DEBOUNCE_DELAY, DEBOUNCE_OPTIONS } from '../../../data/constants';
import GA from '../../../data/GA';
import { StyledButton, StyledList } from '../../common/ButtonsList';

function StepStyle({
  active, next, idx, stepName, prev, title, props, stepCaption,
}: StepProps) {
  const dispatch = useAppDispatch();
  const style = useAppSelector(selectStyle);
  const itemType = useAppSelector(selectItemType);

  const items: StyleI[] = props?.items ?? [];

  const debouncedClickHandler = useCallback(
    debounce((e: React.MouseEvent<HTMLElement>, itemCode: string, secondarySubset: string) => {
      GA.select({ step: stepName, value: itemCode });
      dispatch(updateStyle(itemCode));
      dispatch(updateSecondarySubset(secondarySubset));
      next(e);
    }, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS) as (e: React.MouseEvent<HTMLElement>, itemCode: string, secondarySubset: string) => void,
    [next, stepName, dispatch],
  );

  useEffect(() => {
    if (!active) return;
    GA.stepView(stepName);
  }, [active]);

  return (
    <StepContainer data-automation="style-step">
      <Step id="step">
        <StepTitle
          prev={prev}
          stepTitle={title || `What is the style of your ${itemType.toLowerCase()}?`}
          stepCaption={stepCaption}
          stepNotice="Select an option below"
          optionalProps={props || {}}
        />
        <StepGridContainer>
          <StyledList>
            {items.map((item: StyleI, index: number) => (
              <StyledButton
                data-automation={`style-step-item-${parametrize(item.code)}`}
                key={item.code}
                selected={style === item.code}
                onClick={(e) => {
                  debouncedClickHandler(e, item.code, item.secondarySubset);
                }}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <img
                    src={item.img}
                    alt={item.text}
                    style={
                      index === items.length - 1
                        ? { height: '30px', width: '55px' }
                        : { height: '55px' }
                    }
                  />
                  <Typography variant="body1" textTransform="capitalize">
                    {item.code}
                  </Typography>
                </Stack>
              </StyledButton>
            ))}
          </StyledList>
        </StepGridContainer>
      </Step>
    </StepContainer>
  );
}

export default StepStyle;
